"use client";

import React from 'react';
import { Box } from '@chakra-ui/react';
import ReactECharts from 'echarts-for-react';

interface CircularProgressChartProps {
  value: any;
  suffix?: string;
  primaryColor?: string;
  secondaryColor?: string;
  trendValue?: string;
  trendDirection?: 'up' | 'down';
}

const CircularProgressChart: React.FC<CircularProgressChartProps> = ({
  value,
  suffix = '%',
  primaryColor = '#7987FF',
  secondaryColor = '#79FFB5',
  trendValue = '3%',
  trendDirection = 'up'
}) => {
  // Extract the numeric trend value
  const numericMatch = trendValue.match(/(\d+(\.\d+)?)/);
  const trendPercentage = numericMatch ? parseFloat(numericMatch[0]) : 3;
  
  // Calculate the angle range for each part of the chart
  const totalAngle = 270; // Total angle of the semicircle (180 to -90 degrees)
  
  // Calculate angles
  const emptyAngle = (100 - value) / 100 * totalAngle;
  const valueAngle = (value / 100) * totalAngle;
  const trendAngle = (trendPercentage / 100) * totalAngle;
  
  // Determine the position of trend indicator based on direction
  let blueStartAngle = 180;
  let blueEndAngle = blueStartAngle - valueAngle;
  
  let trendStartAngle, trendEndAngle;
  if (trendDirection === 'up') {
    // For up, position the green segment after the main blue value
    trendStartAngle = blueEndAngle;
    trendEndAngle = trendStartAngle - trendAngle;
  } else {
    // For down, position the red segment before the main blue value
    trendEndAngle = 180;
    trendStartAngle = trendEndAngle + trendAngle;
    // Adjust the blue segment to start after the red trend indicator
    blueStartAngle = trendEndAngle;
    blueEndAngle = blueStartAngle - valueAngle;
  }
  
  // Ensure we don't exceed chart boundaries
  trendEndAngle = Math.max(trendEndAngle, -90);
  blueEndAngle = Math.max(blueEndAngle, -90);
  
  const option = {
    series: [
      // Gray background track
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: -90,
        min: 0,
        max: 100,
        radius: '100%',
        splitNumber: 0,
        axisLine: {
          lineStyle: {
            width: 30,
            color: [
              [1, '#EAEAEA'],
            ]
          }
        },
        pointer: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          fontSize: 30,
          fontWeight: 'bold',
          color: '#165BAA',
          formatter: `{value}${suffix}`,
          offsetCenter: [0, 0],
        },
        data: [{
          value: value,
        }],
        title: {
          show: false,
        }
      },
      // Blue primary progress (main value)
      {
        type: 'gauge',
        startAngle: blueStartAngle,
        endAngle: blueEndAngle,
        min: 0,
        max: 100,
        radius: '100%',
        splitNumber: 0,
        axisLine: {
          lineStyle: {
            width: 30,
            color: [
              [1, primaryColor],
            ],
            shadowBlur: 5,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        },
        pointer: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          show: false,
        },
        data: [{
          value: 100, // Fixed at 100 because we're controlling the angle directly
        }]
      },
      // Trend indicator (green for up, red for down)
      {
        type: 'gauge',
        startAngle: trendStartAngle,
        endAngle: trendEndAngle,
        min: 0,
        max: 100,
        radius: '100%',
        splitNumber: 0,
        axisLine: {
          lineStyle: {
            width: 30,
            color: [
              [1, secondaryColor],
            ],
            shadowBlur: 5,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        },
        pointer: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          show: false,
        },
        data: [{
          value: 100, // Fixed at 100 because we're controlling the angle directly
        }]
      }
    ]
  };

  return (
    <Box 
      height="280px" 
      width="full" 
      display="flex" 
      justifyContent="center" 
      alignItems="center"
    >
      <ReactECharts
        option={option}
        style={{ height: '280px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </Box>
  );
};

export default CircularProgressChart; 